import * as THREE from 'three';
import { BaseCardScene, BaseCardSceneConfig } from './base-card-scene';
import { TurnOverIndicator } from '../components/turn-over-indicator';

export interface VideoCardSceneConfig {
  videoUrl: string;
  cardWidth?: number;
  cardHeight?: number;
  textureRotationRadians?: number;
  sceneId?: string;
  name?: string;
  description?: string;
  defaultScale?: THREE.Vector3;
}

/**
 * Renders a video directly on top of the tracked card.
 */
export class VideoCardScene extends BaseCardScene {
  private config: VideoCardSceneConfig;
  private videoElement: HTMLVideoElement | null = null;
  private videoTexture: THREE.VideoTexture | null = null;
  private videoMesh: THREE.Mesh | null = null;
  private audioUnlocked: boolean = false;
  private volumeButton: HTMLDivElement | null = null;
  private isSceneVisible: boolean = false;
  private turnOverIndicator: TurnOverIndicator | null = null;

  constructor(config: VideoCardSceneConfig) {
    super(
      {
        id: config.sceneId ?? 'video-card',
        name: config.name ?? 'Video Card Overlay',
        description: config.description ?? 'Overlays the promo video directly on the tracked card surface'
      },
      {
        cardWidth: config.cardWidth,
        cardHeight: config.cardHeight,
        defaultScale: config.defaultScale
      }
    );
    this.config = config;
  }

  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
    if (this.initialized) {
      console.warn('VideoCardScene already initialized');
      return;
    }

    if (!this.config.videoUrl) {
      console.error('VideoCardScene requires a videoUrl');
      return;
    }

    this.renderer = renderer;
    parent.add(this.root);

    this.createVideoOverlay();
    this.createVolumeButton();
    this.createTurnOverIndicator();

    this.initialized = true;
  }

  private createVideoOverlay(): void {
    // Create HTMLVideoElement
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = this.config.videoUrl;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.style.display = 'none';
    document.body.appendChild(video);

    this.videoElement = video;
    this.registerAudioUnlock(video);

    // Create texture
    const texture = new THREE.VideoTexture(video);
    texture.encoding = THREE.sRGBEncoding;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    texture.center.set(0.5, 0.5);
    texture.rotation = this.config.textureRotationRadians ?? Math.PI / 2; // rotate to fit landscape card

    this.videoTexture = texture;

    // Use card dimensions from base class
    const geometry = new THREE.PlaneGeometry(this.cardWidth, this.cardHeight);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);

    // Track resources for automatic disposal
    this.trackTexture(texture);
    this.trackGeometry(geometry);
    this.trackMaterial(material);
    // MindAR's anchor already sits on the card's plane, so keep default orientation

    this.videoMesh = mesh;
    this.root.add(mesh);

    // Don't auto-play - wait for scene to become visible
    // Video will be started in setVisible(true)
  }

  /**
   * Register user gesture handler to enable audio once the user interacts
   * Only unlocks audio when the scene is visible (trigger is active)
   */
  private registerAudioUnlock(video: HTMLVideoElement): void {
    if (this.audioUnlocked) return;

    const unlock = () => {
      // Only unlock audio if the scene is currently visible
      if (!this.isSceneVisible) {
        return;
      }

      if (this.audioUnlocked) {
        cleanup();
        return;
      }

      this.audioUnlocked = true;
      video.muted = false;
      this.updateVolumeButtonIcon();  // Update button to show unmuted state
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((err) => {
          console.warn('VideoCardScene: unable to resume video with audio', err);
        });
      }
      cleanup();
      console.log('🔊 Video audio unlocked after user interaction');
    };

    const cleanup = () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('keydown', unlock);
    };

    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
  }

  /**
   * Create volume toggle button
   */
  private createVolumeButton(): void {
    this.volumeButton = document.createElement('div');
    this.volumeButton.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.7);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      border: 2px solid rgba(255, 255, 255, 0.8);
      z-index: 1000;
      transition: all 0.2s ease;
    `;

    this.updateVolumeButtonIcon();

    // Add click handler
    this.volumeButton.onclick = () => {
      if (this.videoElement) {
        this.videoElement.muted = !this.videoElement.muted;
        this.updateVolumeButtonIcon();
      }
    };

    // Add hover effect
    this.volumeButton.onmouseenter = () => {
      if (this.volumeButton) {
        this.volumeButton.style.background = 'rgba(255, 255, 255, 0.9)';
        this.volumeButton.style.transform = 'scale(1.1)';
      }
    };
    this.volumeButton.onmouseleave = () => {
      if (this.volumeButton) {
        this.volumeButton.style.background = 'rgba(0, 0, 0, 0.7)';
        this.volumeButton.style.transform = 'scale(1)';
      }
    };

    document.body.appendChild(this.volumeButton);
  }

  /**
   * Update volume button icon based on muted state
   */
  private updateVolumeButtonIcon(): void {
    if (this.volumeButton && this.videoElement) {
      this.volumeButton.textContent = this.videoElement.muted ? '🔇' : '🔊';
    }
  }

  /**
   * Create turn over indicator at bottom of card
   */
  private createTurnOverIndicator(): void {
    this.turnOverIndicator = new TurnOverIndicator({
      cardWidth: this.cardWidth,
      cardHeight: this.cardHeight,
      text: 'Turn over',
      color: '#00ff00',
      offsetFromBottom: 0.05,
      heightAboveCard: 0.02
    });

    this.root.add(this.turnOverIndicator.getGroup());
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    this.isSceneVisible = visible;  // Track visibility state

    // Show/hide volume button
    if (this.volumeButton) {
      this.volumeButton.style.display = visible ? 'flex' : 'none';
    }

    if (this.videoElement) {
      if (visible) {
        // Start/resume video when scene becomes visible (trigger activated)
        const playPromise = this.videoElement.play();
        if (playPromise) {
          playPromise.catch((err) => {
            console.warn('VideoCardScene: unable to start/resume video playback', err);
          });
        }
      } else {
        // Pause video when scene becomes hidden (trigger lost after grace period)
        this.videoElement.pause();
      }
    }
  }

  update(_delta: number): void {
    // No per-frame logic required yet
  }

  dispose(): void {
    // Remove volume button
    if (this.volumeButton && this.volumeButton.parentElement) {
      this.volumeButton.parentElement.removeChild(this.volumeButton);
      this.volumeButton = null;
    }

    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = '';
      this.videoElement.load();
      this.videoElement.remove();
      this.videoElement = null;
    }

    if (this.turnOverIndicator) {
      this.turnOverIndicator.dispose();
      this.turnOverIndicator = null;
    }

    this.videoMesh = null;
    this.videoTexture = null;

    // Base class dispose will handle tracked textures, materials, and geometries
    super.dispose();
  }
}
